import React, { useState } from "react";
import {
  ActionIcon,
  Anchor,
  Box,
  Group,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { CommissionRecord } from "../../../types/commission";
import {
  StandardTable,
  StandardTableColumn,
} from "../../common/StandardTable/StandardTable";
import {
  MethodBadge,
  bucketDisplay,
  formatEffectiveRange,
  isServiceSpecific,
} from "./commissionDisplay";
import ServiceCommissionsMenu from "./ServiceCommissionsMenu";
import styles from "./PastRatesList.module.css";

export interface PastRate {
  record: CommissionRecord;
  /** The record that superseded it (ends its range). */
  next: CommissionRecord | null;
}

interface PastRatesListProps {
  rates: PastRate[];
  staffId: string;
  canEdit: boolean;
  compact: boolean;
  onEdit: (record: CommissionRecord) => void;
  onDelete: (record: CommissionRecord) => void;
}

type RowProps = Omit<PastRatesListProps, "rates" | "compact"> & {
  rate: PastRate;
  expanded: boolean;
  onToggle: () => void;
};

/** "Service-specific ⌄" — expands the row into the service menu. */
const ServiceSpecificToggle: React.FC<{
  expanded: boolean;
  onToggle: () => void;
}> = ({ expanded, onToggle }) => (
  <Anchor
    component="button"
    type="button"
    size="sm"
    aria-expanded={expanded}
    onClick={onToggle}
  >
    <Group gap={4} wrap="nowrap">
      Service-specific
      {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
    </Group>
  </Anchor>
);

const ServiceValue: React.FC<
  Pick<RowProps, "rate" | "expanded" | "onToggle">
> = ({ rate, expanded, onToggle }) =>
  isServiceSpecific(rate.record) ? (
    <ServiceSpecificToggle expanded={expanded} onToggle={onToggle} />
  ) : (
    <>{bucketDisplay(rate.record, "services") ?? "—"}</>
  );

const RowActions: React.FC<Pick<RowProps, "rate" | "onEdit" | "onDelete">> = ({
  rate,
  onEdit,
  onDelete,
}) => (
  <Group gap={2} wrap="nowrap" justify="flex-end">
    <ActionIcon
      variant="subtle"
      color="gray"
      className={styles.action}
      aria-label="Edit"
      onClick={() => onEdit(rate.record)}
    >
      <IconPencil size={16} />
    </ActionIcon>
    <ActionIcon
      variant="subtle"
      color="gray"
      className={`${styles.action} ${styles.deleteAction}`}
      aria-label="Delete"
      onClick={() => onDelete(rate.record)}
    >
      <IconTrash size={16} />
    </ActionIcon>
  </Group>
);

/** Desktop: the rate's table row, plus its service menu when expanded. */
const PastRateRow: React.FC<RowProps & { colSpan: number }> = (props) => {
  const { rate, canEdit, expanded, colSpan, staffId } = props;
  return (
    <>
      <Table.Tr>
        <Table.Td style={{ whiteSpace: "nowrap" }}>
          {formatEffectiveRange(rate.record, rate.next)}
        </Table.Td>
        <Table.Td>
          <ServiceValue {...props} />
        </Table.Td>
        <Table.Td>{bucketDisplay(rate.record, "products") ?? "—"}</Table.Td>
        <Table.Td>
          <MethodBadge commission={rate.record} />
        </Table.Td>
        {canEdit && (
          <Table.Td>
            <RowActions {...props} />
          </Table.Td>
        )}
      </Table.Tr>
      {expanded && (
        <Table.Tr className={styles.expandedRow}>
          <Table.Td colSpan={colSpan}>
            <ServiceCommissionsMenu record={rate.record} staffId={staffId} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
};

/** Phones: a card per past rate (no sideways scrolling). */
const PastRateCard: React.FC<RowProps> = (props) => {
  const { rate, canEdit, expanded, staffId } = props;
  return (
    <Box className={styles.card}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={6}>
          <Text size="sm" fw={600}>
            {formatEffectiveRange(rate.record, rate.next)}
          </Text>
          <MethodBadge commission={rate.record} />
          <Group gap={6}>
            <Text size="xs" c="dimmed">
              Services:
            </Text>
            <Text component="div" size="sm">
              <ServiceValue {...props} />
            </Text>
          </Group>
          <Group gap={6}>
            <Text size="xs" c="dimmed">
              Products:
            </Text>
            <Text size="sm">
              {bucketDisplay(rate.record, "products") ?? "—"}
            </Text>
          </Group>
        </Stack>
        {canEdit && <RowActions {...props} />}
      </Group>
      {expanded && (
        <Box mt="sm">
          <ServiceCommissionsMenu record={rate.record} staffId={staffId} />
        </Box>
      )}
    </Box>
  );
};

const COLUMNS: StandardTableColumn<PastRate>[] = [
  { key: "effective", label: "Effective" },
  { key: "service", label: "Service" },
  { key: "product", label: "Product" },
  { key: "method", label: "Method" },
];

/** Earlier commission rates: when each applied and its terms. A
 * service-specific rate expands into its full service menu. */
const PastRatesList: React.FC<PastRatesListProps> = ({
  rates,
  compact,
  ...rowProps
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const rowState = (rate: PastRate) => ({
    rate,
    expanded: expanded.has(rate.record.id),
    onToggle: () => toggle(rate.record.id),
  });

  if (rates.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        No past rates.
      </Text>
    );
  }
  if (compact) {
    return (
      <Stack gap="sm">
        {rates.map((rate) => (
          <PastRateCard
            key={rate.record.id}
            {...rowProps}
            {...rowState(rate)}
          />
        ))}
      </Stack>
    );
  }
  const columns = rowProps.canEdit
    ? [...COLUMNS, { key: "actions", width: 80 }]
    : COLUMNS;
  return (
    <StandardTable<PastRate>
      columns={columns}
      data={rates}
      getRowKey={(rate) => rate.record.id}
      renderRow={(rate) => (
        <PastRateRow
          {...rowProps}
          {...rowState(rate)}
          colSpan={columns.length}
        />
      )}
      ariaLabel="Past commission rates"
    />
  );
};

export default PastRatesList;
